import { useState } from "react"
import { PanelLeft, Search } from "lucide-react"
import {
  BackArrowIcon,
  ChevronDownIcon,
  SettingsIcon,
  TeamNavIcon
} from "./Icons"
import { HiddenField, MethodField } from "../lib/rails"
import { useDismissiblePopover } from "../lib/useDismissiblePopover"
import { LoadingOrb } from "./LoadingOrb"
import { ConceptiveConstellationMark } from "./ConceptiveConstellationMark"

export function LogoMark({ className = "" }) {
  return <ConceptiveConstellationMark className={`vibecheck-logo ${className}`.trim()} />
}

export function BrandLockup({ href = "/", kicker }) {
  return (
    <a href={href} className="brand-lockup">
      <LogoMark className="brand-mark brand-mark-small" />
      <div>
        <div className="brand-name-small">Conceptive</div>
        {kicker ? <div className="brand-kicker">{kicker}</div> : null}
      </div>
    </a>
  )
}

export function LogoutForm({ action, csrfToken, className, label = "Log out" }) {
  return (
    <form className="button_to" method="post" action={action}>
      <MethodField method="delete" />
      <HiddenField name="authenticity_token" value={csrfToken} />
      <button type="submit" className={className}>{label}</button>
    </form>
  )
}

// The sidebar body belongs to the issue rail (passed in via the `rail` slot),
// and its settings menu holds repository context and account controls.
const workspaceUtilityNavItems = [
  { id: "team", label: "Team", Icon: TeamNavIcon }
]

export const workspaceSectionRouteSegments = {
  domains: "context",
  concept: "concept",
  team: "team"
}

const workspaceSectionIds = new Set(Object.keys(workspaceSectionRouteSegments))
const workspaceSectionsByRouteSegment = Object.fromEntries(
  Object.entries(workspaceSectionRouteSegments).map(([section, segment]) => [segment, section])
)

export function normalizeWorkspaceSection(value, fallback = "concept") {
  const section = String(value || "").replace(/^#/, "")
  if (section === "stack") return "concept"

  if (workspaceSectionIds.has(section)) return section
  if (workspaceSectionsByRouteSegment[section]) return workspaceSectionsByRouteSegment[section]

  return fallback
}

export function workspaceSectionHref(repository, section, rootPath = "/") {
  const normalizedSection = normalizeWorkspaceSection(section, null)
  if (!normalizedSection) return rootPath
  if (!repository?.workspaceSlug || !repository.fullName) return rootPath

  const workspaceSlug = encodeURIComponent(repository.workspaceSlug)
  const repositorySlug = repository.fullName.split("/").map(encodeURIComponent).join("/")
  const sectionSegment = workspaceSectionRouteSegments[normalizedSection]

  return `/workspaces/${workspaceSlug}/repositories/${repositorySlug}/${sectionSegment}`
}

export function workspaceSectionPathsFor(repository, rootPath = "/") {
  return Object.fromEntries(
    Object.keys(workspaceSectionRouteSegments).map((section) => (
      [section, workspaceSectionHref(repository, section, rootPath)]
    ))
  )
}

export function readSidebarCollapsedState() {
  return false
}

export function writeSidebarCollapsedState() {
}

export function shouldHandleClientNavigation(event) {
  if (!event) return false
  if (event.defaultPrevented) return false
  if (event.button && event.button !== 0) return false
  if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) return false

  return true
}

export function RepositoryPicker({ className = "", repositories = [], repository, rootPath = "/" }) {
  const [open, setOpen] = useState(false)
  const pickerRef = useDismissiblePopover(open, () => setOpen(false))
  const fullName = repository?.fullName
  const [, repoName] = fullName ? splitRepositoryFullName(fullName) : []
  const avatarUrl = repositoryAvatarUrl(fullName)

  if (!fullName) return null

  return (
    <div className={`repository-context-picker ${className}`.trim()} ref={pickerRef}>
      <button
        type="button"
        className="repository-context-trigger"
        data-sidebar-label={repoName || fullName}
        aria-label={`Switch repository. Current repository: ${fullName}`}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((current) => !current)}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="" aria-hidden="true" className="repository-context-avatar" />
        ) : null}
        <span className="repository-context-copy">
          <span className="repository-context-name">{repoName || fullName}</span>
        </span>
        <ChevronDownIcon className="repository-context-chevron" />
      </button>

      {open ? (
        <RepositorySwitchMenu repositories={repositories} repository={repository} rootPath={rootPath} />
      ) : null}
    </div>
  )
}

export function RepositorySwitchMenu({ className = "", repositories = [], repository, rootPath = "/" }) {
  const fullName = repository?.fullName
  const options = repositoryOptions(repository, repositories)

  return (
    <div className={`repository-context-menu ${className}`.trim()} role="menu" aria-label="Switch repository">
      <a href={rootPath} className="repository-context-menu-item is-link" role="menuitem">
        <span className="repository-context-menu-copy">
          <span className="repository-context-menu-name">All repositories</span>
          <span className="repository-context-menu-meta">Choose another repo</span>
        </span>
      </a>

      {options.map((option) => {
        const current = option.fullName === fullName

        return (
          <a
            key={option.fullName}
            href={repositoryHref(option, rootPath)}
            className={`repository-context-menu-item ${current ? "is-active" : ""}`.trim()}
            aria-current={current ? "page" : undefined}
            role="menuitem"
          >
            <span className="repository-context-menu-copy">
              <span className="repository-context-menu-name">{option.fullName}</span>
              <span className="repository-context-menu-meta">{option.clonedAt ? "Cloned" : "Opened"}</span>
            </span>
            {current ? <span className="repository-context-active-dot" aria-hidden="true"></span> : null}
          </a>
        )
      })}
    </div>
  )
}

function splitRepositoryFullName(fullName) {
  const slash = fullName.indexOf("/")
  if (slash === -1) return [null, fullName]
  return [fullName.slice(0, slash), fullName.slice(slash + 1)]
}

function repositoryAvatarUrl(fullName) {
  const [ownerLogin] = fullName ? splitRepositoryFullName(fullName) : []
  return ownerLogin ? `https://github.com/${encodeURIComponent(ownerLogin)}.png?size=64` : null
}

function repositoryOptions(repository, repositories) {
  const workspaceSlug = repository?.workspaceSlug
  const seen = new Set()

  return [repository, ...repositories].filter(Boolean).reduce((options, option) => {
    if (!option.fullName || seen.has(option.fullName)) return options

    seen.add(option.fullName)
    options.push({
      ...option,
      workspaceSlug: option.workspaceSlug || workspaceSlug
    })

    return options
  }, [])
}

function repositoryHref(repository, rootPath) {
  if (!repository.workspaceSlug || !repository.fullName) return rootPath

  const workspaceSlug = encodeURIComponent(repository.workspaceSlug)
  const repositorySlug = repository.fullName.split("/").map(encodeURIComponent).join("/")

  return `/workspaces/${workspaceSlug}/repositories/${repositorySlug}`
}

export function WorkspaceSidebar({
  activeSection,
  collapsed = true,
  csrfToken,
  githubLogin,
  mobileMenuOpen = false,
  mobileVisible = false,
  onNavigate,
  onSearch = null,
  onOpenContext = null,
  onToggleMobileMenu,
  onToggleCollapsed,
  paths,
  rail = null,
  replayExit = null,
  repositories = [],
  repository,
  rootPath = "/",
  sectionPaths = {},
  syncMenu = null
}) {
  const [showAccountMenu, setShowAccountMenu] = useState(false)
  const [showRepositoryMenu, setShowRepositoryMenu] = useState(false)
  const [showSyncMenu, setShowSyncMenu] = useState(false)
  const accountMenuRef = useDismissiblePopover(showAccountMenu, () => closeAccountMenu())
  const repositoryFullName = repository?.fullName
  const [, repositoryName] = repositoryFullName ? splitRepositoryFullName(repositoryFullName) : []
  const repositoryAvatar = repositoryAvatarUrl(repositoryFullName)
  const teamHref = sectionPaths.team || workspaceSectionHref(repository, "team", rootPath)

  function closeAccountMenu() {
    setShowAccountMenu(false)
    setShowRepositoryMenu(false)
    setShowSyncMenu(false)
  }

  function toggleAccountMenu() {
    setShowAccountMenu((current) => {
      if (current) {
        setShowRepositoryMenu(false)
        setShowSyncMenu(false)
      }
      return !current
    })
  }

  function handleUtilityNavigate(id, event) {
    onNavigate?.(id, event)
    if (!event || event.defaultPrevented) closeAccountMenu()
  }

  return (
    <aside className={`workspace-sidebar ${mobileVisible ? "is-mobile-visible" : ""} ${mobileMenuOpen ? "is-mobile-menu-open" : ""}`.trim()} aria-label="Workspace navigation">
      <div className="sidebar-top">
        <div className="sidebar-brand-row">
          <button
            type="button"
            className="sidebar-brand-toggle"
            aria-label={replayExit ? "Exit replay" : "Open concepts"}
            title={replayExit ? "Exit replay" : "Open concepts"}
            onClick={(event) => replayExit ? replayExit() : onNavigate?.("concept", event)}
          >
            <span className="sidebar-logo-slot">
              <LogoMark className="sidebar-logo" />
            </span>
            <span className="sidebar-brand-wordmark">Conceptive</span>
          </button>
          {!replayExit ? (
            <div className="sidebar-header-actions">
              {onSearch ? (
                <button
                  type="button"
                  className="sidebar-header-button sidebar-search-trigger"
                  aria-label="Search items"
                  title="Search items"
                  onClick={onSearch}
                >
                  <Search aria-hidden="true" strokeWidth={1.7} />
                </button>
              ) : null}
              <button
                type="button"
                className="sidebar-header-button sidebar-navigation-toggle"
                aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
                aria-expanded={!collapsed}
                title={collapsed ? "Expand navigation" : "Collapse navigation"}
                onClick={() => onToggleCollapsed?.()}
              >
                <PanelLeft aria-hidden="true" strokeWidth={1.7} />
              </button>
              <button
                type="button"
                className="sidebar-header-button sidebar-mobile-brand-toggle"
                aria-label={mobileMenuOpen ? "Close navigation" : "Open navigation"}
                aria-expanded={mobileMenuOpen}
                title={mobileMenuOpen ? "Close navigation" : "Open navigation"}
                onClick={() => onToggleMobileMenu?.()}
              >
                <PanelLeft aria-hidden="true" strokeWidth={1.7} />
              </button>
            </div>
          ) : null}
        </div>

        {rail}
      </div>

      <div className="sidebar-footer">
        <div className="sidebar-account" id="account" ref={accountMenuRef}>
          <div className="sidebar-account-row">
            <strong>{githubLogin}</strong>

            <button
              type="button"
              className="sidebar-account-trigger"
              data-sidebar-label={replayExit ? "Exit replay" : "Settings"}
              aria-label={replayExit ? "Exit replay" : "Open workspace menu"}
              aria-expanded={replayExit ? undefined : showAccountMenu}
              aria-haspopup={replayExit ? undefined : "menu"}
              onClick={replayExit ? () => replayExit() : toggleAccountMenu}
            >
              <SettingsIcon />
            </button>
          </div>

          {showAccountMenu ? (
            <div className="sidebar-account-menu" role="menu" aria-label="Workspace options">
              {repositoryFullName ? (
                <button
                  type="button"
                  className="sidebar-account-menu-item sidebar-account-repository-trigger"
                  role="menuitem"
                  aria-haspopup="menu"
                  aria-expanded={showRepositoryMenu}
                  onClick={() => setShowRepositoryMenu((current) => !current)}
                >
                  {repositoryAvatar ? (
                    <img src={repositoryAvatar} alt="" aria-hidden="true" className="sidebar-account-menu-avatar" />
                  ) : null}
                  <span className="sidebar-account-menu-copy">
                    <span className="sidebar-account-menu-label">{repositoryName || repositoryFullName}</span>
                  </span>
                  <ChevronDownIcon className="sidebar-account-menu-chevron" />
                </button>
              ) : (
                <a href={rootPath} className="sidebar-account-menu-item sidebar-account-repository-trigger" role="menuitem">
                  <span className="sidebar-account-menu-copy">
                    <span className="sidebar-account-menu-label">Repositories</span>
                    <span className="sidebar-account-menu-meta">Choose a repo</span>
                  </span>
                </a>
              )}

              {onOpenContext ? (
                <button
                  type="button"
                  className="sidebar-account-menu-item"
                  role="menuitem"
                  onClick={() => { closeAccountMenu(); onOpenContext() }}
                >
                  Context.md
                </button>
              ) : null}

              {repositoryFullName && syncMenu ? (
                <button
                  type="button"
                  className="sidebar-account-menu-item sidebar-account-sync-trigger"
                  role="menuitem"
                  aria-haspopup="menu"
                  aria-expanded={showSyncMenu}
                  onClick={() => {
                    setShowRepositoryMenu(false)
                    setShowSyncMenu((current) => !current)
                  }}
                >
                  Sync
                  <ChevronDownIcon className="sidebar-account-menu-chevron sidebar-account-sync-chevron" />
                </button>
              ) : null}

              {workspaceUtilityNavItems.filter(({ id }) => !repository?.readOnly || id !== "team").map(({ id, label }) => (
                <a
                  key={id}
                  href={id === "team" ? teamHref : workspaceSectionHref(repository, id, rootPath)}
                  className="sidebar-account-menu-item"
                  role="menuitem"
                  aria-current={activeSection === id ? "page" : undefined}
                  onClick={(event) => handleUtilityNavigate(id, event)}
                >
                  {label}
                </a>
              ))}

              <LogoutForm
                action={paths.githubSession}
                csrfToken={csrfToken}
                className="sidebar-account-menu-item"
                label="Log out"
              />
            </div>
          ) : null}
          {showAccountMenu && showRepositoryMenu && repositoryFullName ? (
            <RepositorySwitchMenu
              className="sidebar-repository-switch-menu"
              repositories={repositories}
              repository={repository}
              rootPath={rootPath}
            />
          ) : null}
          {showAccountMenu && showSyncMenu && repositoryFullName && syncMenu ? (
            <WorkspaceSyncSubmenu
              className="sidebar-sync-menu"
              {...syncMenu}
              onSyncNow={() => {
                const result = syncMenu.onSyncNow?.()
                closeAccountMenu()
                return result
              }}
              onFrequencySelect={(option) => {
                const result = syncMenu.onFrequencySelect?.(option)
                closeAccountMenu()
                return result
              }}
            />
          ) : null}
        </div>
      </div>
    </aside>
  )
}

export function WorkspaceSyncSubmenu({
  className = "",
  credentialSetupPath = null,
  frequency = 100,
  frequencyOptions = [50, 100, 200],
  frequencySaving = false,
  onFrequencySelect,
  onSyncNow,
  syncBusy = false,
  syncDisabled = false,
  syncDisabledReason = null,
  syncStatus = null
}) {
  return (
    <div className={`workspace-sync-submenu ${className}`.trim()} role="menu" aria-label="Sync options">
      <button
        type="button"
        className={`workspace-sync-now ${syncBusy ? "is-busy" : ""}`.trim()}
        disabled={syncDisabled}
        onClick={onSyncNow}
        role="menuitem"
      >
        {syncBusy ? (
          <>
            <LoadingOrb state="working" />
            <span>Syncing — view progress</span>
          </>
        ) : "Sync now"}
      </button>
      {syncDisabled && syncDisabledReason ? (
        <span className="workspace-sync-status">
          {syncDisabledReason}{credentialSetupPath ? <> <a href={credentialSetupPath}>Manage credentials</a></> : null}
        </span>
      ) : null}
      {syncStatus ? <span className="workspace-sync-status">{syncStatus}</span> : null}

      <div className="workspace-sync-divider" role="separator"></div>

      <div className="workspace-sync-frequency" role="group" aria-label="Sync frequency">
        <span className="workspace-sync-frequency-label">Sync frequency</span>
        <div className="workspace-sync-frequency-options">
          {frequencyOptions.map((option) => {
            const selected = Number(option) === Number(frequency)

            return (
              <button
                type="button"
                key={option}
                className={`workspace-sync-frequency-option ${selected ? "is-selected" : ""}`.trim()}
                disabled={frequencySaving}
                aria-pressed={selected}
                onClick={() => onFrequencySelect?.(option)}
              >
                {option} commits
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export function Notice({ alert, className = "" }) {
  if (!alert) return null

  return <div className={`notice notice-alert ${className}`.trim()}>{alert}</div>
}

export function BackButton({ href, iconOnly = false, label = "Back", onClick }) {
  const content = (
    <>
      <BackArrowIcon />
      {iconOnly ? null : <span>{label}</span>}
    </>
  )

  if (href) {
    return (
      <a href={href} className="subtle-back-button" aria-label={label}>
        {content}
      </a>
    )
  }

  return (
    <button type="button" className="subtle-back-button" onClick={onClick} aria-label={label}>
      {content}
    </button>
  )
}

export function FooterMark({ showWordmark = false }) {
  return (
    <div className="source-picker-footer-mark">
      <div className="source-picker-footer-logo-shell" aria-hidden="true">
        <LogoMark className="source-picker-footer-logo" />
      </div>
      {showWordmark ? <div className="source-picker-footer-wordmark">Conceptive</div> : null}
    </div>
  )
}

export function SlideUp({ children, className = "", delay }) {
  const style = delay ? { animationDelay: delay } : undefined

  return (
    <div className={`slide-up ${className}`.trim()} style={style}>
      {children}
    </div>
  )
}
